// TeacherFeaturesStep.js - Overview of teacher tools
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../i18n/useTranslation';

const TEACHER_FEATURES = [
  {
    icon: 'clipboard-plus-outline',
    title: 'Create Tests & Quizzes',
    description: 'Build custom assessments from our question bank or create your own',
  },
  {
    icon: 'account-group',
    title: 'Manage Students',
    description: 'Add students to your classes and track their individual progress',
  },
  {
    icon: 'chart-box-outline',
    title: 'View Analytics',
    description: 'Get insights into student engagement, time spent, and performance',
  },
  {
    icon: 'folder-multiple-outline',
    title: 'Build Collections',
    description: 'Curate custom learning paths with topics, videos, and PWOs',
  },
];

const TeacherFeaturesStep = ({ onNext, onBack }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const colors = {
    bg: isDark ? '#1e293b' : '#f8fafc',
    card: isDark ? '#334155' : '#ffffff',
    text: isDark ? '#f8fafc' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    accent: '#6366f1',
    accentLight: isDark ? '#6366f130' : '#6366f115',
    border: isDark ? '#475569' : '#e2e8f0',
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.bg }]}>
      <View style={[styles.content, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Title */}
        <Text style={[styles.title, { color: colors.text }]}>
          {t('Your Teaching Tools')}
        </Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          {t("Here's what you can do with 3D AI Tutor")}
        </Text>

        {/* Features List */}
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.featuresContainer}
          showsVerticalScrollIndicator={false}
        >
          {TEACHER_FEATURES.map((feature, index) => (
            <View
              key={index}
              style={[styles.featureCard, { backgroundColor: colors.bg, borderColor: colors.border }]}
            >
              <View style={[styles.featureIcon, { backgroundColor: colors.accentLight }]}>
                <MaterialCommunityIcons name={feature.icon} size={28} color={colors.accent} />
              </View>
              <View style={styles.featureText}>
                <Text style={[styles.featureTitle, { color: colors.text }]}>
                  {t(feature.title)}
                </Text>
                <Text style={[styles.featureDescription, { color: colors.textSecondary }]}>
                  {t(feature.description)}
                </Text>
              </View>
            </View>
          ))}
        </ScrollView>

        {/* Navigation Buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.secondaryButton, { borderColor: colors.border }]}
            onPress={onBack}
            activeOpacity={0.7}
          >
            <MaterialCommunityIcons name="arrow-left" size={18} color={colors.text} />
            <Text style={[styles.secondaryButtonText, { color: colors.text }]}>{t('Back')}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.accent }]}
            onPress={onNext}
            activeOpacity={0.8}
          >
            <Text style={styles.primaryButtonText}>{t('Continue')}</Text>
            <MaterialCommunityIcons name="arrow-right" size={18} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 16,
    padding: 32,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
    maxHeight: '90%',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 24,
  },
  scrollView: {
    maxHeight: 340,
  },
  featuresContainer: {
    gap: 12,
    paddingBottom: 8,
  },
  featureCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 14,
  },
  featureIcon: {
    width: 52,
    height: 52,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  featureText: {
    flex: 1,
  },
  featureTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  featureDescription: {
    fontSize: 13,
    lineHeight: 18,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 24,
    gap: 12,
  },
  secondaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    gap: 6,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  primaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    gap: 6,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
});

export default TeacherFeaturesStep;
